import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Person as UserIcon,
  Business as VendorIcon,
  ShoppingCart as StockInIcon,
  ShoppingCartCheckout as StockOutIcon,
  Menu as MenuIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Logout as LogoutIcon,
    LocalShipping as TransportIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';

const Header = ({ onMenuToggle, isMenuOpen, onSidebarToggle, isSidebarCollapsed }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const [isAddNewOpen, setIsAddNewOpen] = useState(false);
  const dropdownRef = useRef(null);
  const addNewRef = useRef(null);
  const navigate = useNavigate();
  const { user, logout, getCurrentUser, getAccessToken } = useAuth();

  // Debug: Log user data when it changes
  useEffect(() => {
    console.log('=== HEADER USER DATA ===');
    console.log('Current user from context:', user);
    console.log('Username:', user?.username);
    console.log('Access token available:', !!getAccessToken());
    console.log('=== END HEADER USER DATA ===');
  }, [user, getAccessToken]);

  const handleLogout = () => {
    logout();
    setIsLogoutOpen(false);
    // Redirect to login page
    navigate('/');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsLogoutOpen(false);
      }
      if (addNewRef.current && !addNewRef.current.contains(event.target)) {
        setIsAddNewOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleAddNewClick = (type) => {
    console.log(`Add new ${type} clicked`);
    setIsAddNewOpen(false);

    // Navigate to appropriate page and trigger modal opening
    switch (type) {
      case 'User':
        navigate('/userDetails', { state: { openModal: true } });
        break;
      case 'Vendor':
        navigate('/vendors', { state: { openModal: true } });
        break;
      case 'Stock In':
        navigate('/stock-in', { state: { openModal: true } });
        break;
      case 'Stock Out':
        navigate('/stock-out', { state: { openModal: true } });
        break;
      case 'Transport':
        navigate('/transport', { state: { openModal: true } });
        break;
      case 'Advance Details':
        navigate('/advance-details', { state: { openModal: true } });
        break;
      default:
        console.log(`No route defined for ${type}`);
    }
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
      <div className="">
        <div className="flex justify-between items-center h-16">
          {/* Left side - Logo and Menu buttons */}
          <div className="flex items-center space-x-4">


            {/* Mobile menu button */}
            <button
              onClick={onMenuToggle}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 lg:hidden"
              aria-label="Toggle mobile menu"
            >
              <MenuIcon className="h-6 w-6" />
            </button>


            {/* Desktop sidebar toggle button */}
            <button
              onClick={onSidebarToggle}
              className="hidden lg:block p-2 rounded-md text-gray-600 hover:text-gray-900 "
              aria-label="Toggle sidebar"
            >
              <MenuIcon className="h-6 w-6" />
            </button>
            {/* Logo */}
            <div className="flex items-center">
              <img
                src="/assets/img/logo.jpg"
                alt="TKD zLogo"
                className="h-8 w-auto"
              />
            </div>
          </div>

          {/* Right side - Add New, Settings, User icon, and Administrator name */}
          <div className="flex items-center space-x-4 px-4 py-6 ">
            {/* Add New button */}
            <div className="relative" ref={addNewRef}>
              <button
                onClick={() => setIsAddNewOpen(!isAddNewOpen)}
                className="flex items-center px-4 py-2 btn-primary text-white  rounded-md "
              >

                Add New
              </button>

              {/* Add New Modal */}
              {isAddNewOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 py-4 z-50" style={{ width: '500px' }}>
                  <div className="px-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Add New</h3>
                    <div className="grid grid-cols-4 gap-4">
                      {/* User Card */}
                      <button
                        onClick={() => handleAddNewClick('User')}
                        className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-3">
                          <UserIcon className="w-6 h-6 text-blue-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-900">User</span>

                      </button>



                      {/* Stock In Card */}
                      <button
                        onClick={() => handleAddNewClick('Stock In')}
                        className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-3">
                          <StockInIcon className="w-6 h-6 text-blue-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-900">Stock In</span>
                      </button>

                      {/* Stock Out Card */}
                      <button
                        onClick={() => handleAddNewClick('Stock Out')}
                        className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-3">
                          <StockOutIcon className="w-6 h-6 text-blue-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-900">Stock Out</span>
                      </button>

                      {/* Transport Card */}
                      <button
                        onClick={() => handleAddNewClick('Transport')}
                        className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-3">
                          <TransportIcon className="w-6 h-6 text-blue-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-900">Transport</span>
                      </button>

                      {/* Vendor Card */}
                      <button
                        onClick={() => handleAddNewClick('Vendor')}
                        className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-3">
                          <VendorIcon className="w-6 h-6 text-blue-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-900">Vendor</span>
                      </button>
                      {/* Advance Details Card */}
                      <button
                        onClick={() => handleAddNewClick('Advance Details')}
                        className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-3">
                          <AccountBalanceIcon className="w-6 h-6 text-blue-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-900">Advance Details</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* User icon and username with dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsLogoutOpen(!isLogoutOpen)}
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 "
              >
                {/* User icon */}
                <div className="p-2 text-gray-600">
                  <AccountCircleIcon className="h-6 w-6" />
                </div>

                {/* Username label */}
                <span className="text-gray-700 font-medium font-poppins">{user?.username || 'User'}</span>
                {console.log('Displaying username:', user?.username)} {/* Debug log */}

                {/* Dropdown arrow */}
                <KeyboardArrowDownIcon
                  className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isLogoutOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* User dropdown */}
              {isLogoutOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                  {/* User info section */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-gray-100 rounded-full">
                        <AccountCircleIcon className="h-6 w-6 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{user?.username || 'User'}</p>
                        <p className="text-xs text-gray-500 text-weight-bold"> {user?.roles?.length ? user.roles.join(', ') : 'Logged in'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Logout button */}
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                  >
                    <LogoutIcon className="h-4 w-4 mr-3 text-gray-400" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;